/*
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import React from "react";
//Custom libraries
import { OrderItemTable } from "../../widgets/order-item-table";
import FormattedPriceDisplay from "../../widgets/formatted-price-display";
//UI
import { Divider } from "@mui/material";
import {
  StyledBox,
  StyledButton,
  StyledGrid,
  StyledPaper,
  StyledTypography,
} from "@hcl-commerce-store-sdk/react-component";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { loginStatusSelector } from "redux/selectors/user";

interface MiniCartContentProps {
  title: string;
  orderType: string;
  orderItems: any[];
  subtotalLabel: string;
  subtotal: number | null;
  subtotalCurrency: string;
  subtotalWithoutTax: number | null;
  emptyCartMsg: string;
  cartLinkLabel: string;
  checkoutLinkLabel: string;
  handleCartOnClick: (e?: any) => void;
  handleCheckoutOnClick: (e?: any) => void;
  handleClose: (e?: any) => void;
  isOrderOwner: boolean;
  isB2B: boolean;
}

const StyledMiniCartContent = React.forwardRef<any, MiniCartContentProps>((props: any, ref: any) => {
  const {
    title,
    orderType,
    orderItems,
    subtotalLabel,
    subtotalCurrency,
    subtotal,
    subtotalWithoutTax,
    emptyCartMsg,
    cartLinkLabel,
    handleCartOnClick,
    handleClose,
    isOrderOwner,
    isB2B,
  } = props;
  const { t } = useTranslation();
  const loginStatus = useSelector(loginStatusSelector);
  return (
    <StyledPaper ref={ref} className="mini-cart-container" data-testid="mini-cart-popper">
      <StyledButton
        testId="handle-cart-close-on-click"
        color="secondary"
        className="close-minicart-button"
        onClick={handleClose}>
        ✕
      </StyledButton>
      {!isB2B ? (
        <StyledTypography variant="h6" className="horizontal-padding-2 top-padding-2">
          {title}
        </StyledTypography>
      ) : (
        <>
          <StyledTypography
            variant="h6"
            style={{ overflowWrap: "break-word" }}
            className="horizontal-padding-2 top-padding-2">
            {title}
          </StyledTypography>
          <StyledTypography variant="body1" className="horizontal-padding-2 bottom-padding-2">
            {orderType}
          </StyledTypography>
        </>
      )}
      <Divider className="heading" />
      {orderItems.length > 0 ? (
        <>
          <StyledBox>
            <OrderItemTable data={orderItems} preShip={true} miniCartView={true} handleMiniCartClose={handleClose} />
          </StyledBox>
          <Divider />
          <StyledGrid container className="horizontal-padding-2 vertical-padding-2">
            <StyledGrid item xs={7}>
              <StyledTypography variant="body1">{subtotalLabel}</StyledTypography>
            </StyledGrid>
            <StyledGrid item xs={5}>
              <StyledTypography variant="body1" align="right" style={{ fontWeight: "bold" }}>
                {loginStatus && <FormattedPriceDisplay min={subtotalWithoutTax} currency={subtotalCurrency} />}
                {!loginStatus && <FormattedPriceDisplay min={subtotal} currency={subtotalCurrency} />}
              </StyledTypography>
              {loginStatus && (
                <StyledTypography variant="body1" align="right" style={{ fontSize: "10px" }}>
                  {t("OrderItemTable.Labels.Alv", { value: 0 })}
                </StyledTypography>
              )}
            </StyledGrid>
          </StyledGrid>
        </>
      ) : (
        <StyledTypography align="center" className="horizontal-padding-2 vertical-padding-10">
          {emptyCartMsg}
        </StyledTypography>
      )}
      <Divider />
      <StyledBox className="horizontal-padding-2 vertical-padding-1" textAlign="center">
        {isB2B && !isOrderOwner ? (
          <StyledButton testId="handle-cart-on-click" color="secondary" onClick={handleCartOnClick}>
            {cartLinkLabel}
          </StyledButton>
        ) : (
          <>
            <StyledButton
              testId="handle-cart-on-click"
              color="secondary"
              className="right-margin-1"
              onClick={handleCartOnClick}>
              {cartLinkLabel}
            </StyledButton>
          </>
        )}
      </StyledBox>
    </StyledPaper>
  );
});

export default StyledMiniCartContent;
